import React, { useEffect, useState } from "react";
import {
  Product,
  ProductProvider,
  useProductBase,
  useProductProviderContext,
} from "@made-people/grease-product";
import {
  useCentraContext,
  useCentraSearchFunction,
} from "@made-people/grease-centra";
import { crc32 } from "@made-people/functions-crc32";
import { CentraGetProductsPayload } from "@made-people/centra-checkout-api";

export const AlgoliaCentraProductWrapper: React.FC<{
  children: React.ReactNode;
  isDrawerOpen: boolean;
}> = ({ children, isDrawerOpen }) => {
  const algoliaProductBase = useProductBase();
  const productId = algoliaProductBase.productId;

  const initialPayload: CentraGetProductsPayload = {
    products: [productId],
    limit: 1,
  };

  const providerContext = useProductProviderContext();
  const { queryContext } = useCentraContext();
  const search = useCentraSearchFunction({
    payload: {
      products: {
        product: productId,
      },
    },
  });

  const [centraProduct, setCentraProduct] = useState<Product | undefined>(
    undefined,
  );
  const queryHash = crc32(JSON.stringify({ initialPayload, queryContext }));

  useEffect(() => {
    if (!isDrawerOpen) return;
    let cancelled = false;

    async function _search() {
      const results = await search(initialPayload);
      if (cancelled) {
        return;
      }
      const result = results.items[0];
      if (!result || result.type !== "product") return;
      setCentraProduct(result.product);
    }
    _search();

    return () => {
      cancelled = true;
    };
  }, [queryHash, isDrawerOpen]);

  if (providerContext.type === "pdp") {
    console.warn("Using AlgoliaCentraProductWrapper in PDP context");
    return children;
  }

  if (!centraProduct) {
    return (
      <div className="p-4 min-w-[300px] lg:min-w-[448px] animate-pulse">
        <div className="flex gap-4 mb-6">
          <div className="w-20 h-24 bg-surface-2 rounded" />
          <div className="flex-1">
            <div className="h-4 bg-surface-2 rounded w-3/4 mb-2" />
            <div className="h-4 bg-surface-2 rounded w-1/4" />
          </div>
        </div>
        <div className="h-4 bg-surface-2 rounded w-1/3 mb-3" />
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-10 bg-surface-2 rounded" />
          ))}
        </div>
        <div className="h-12 bg-surface-2 rounded w-full" />
      </div>
    );
  }

  return (
    <ProductProvider product={centraProduct} context={{ type: "unknown" }}>
      {children}
    </ProductProvider>
  );
};
